/**
 * Helper functions for subscription validation and status
 */

/**
 * Verifica se una subscription è valida per la data specificata
 * @param {Object} subscription - L'oggetto subscription
 * @param {Date} [date] - La data da verificare (default: oggi)
 * @returns {boolean} - True se la subscription è valida
 */
export const isSubscriptionValid = (subscription, date = new Date()) => {
  if (!subscription || !subscription.startDate || !subscription.endDate) {
    return false;
  }

  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);

  const startDate = new Date(subscription.startDate);
  const endDate = new Date(subscription.endDate);
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  if (checkDate < startDate || checkDate > endDate) {
    return false;
  }

  // Se l'iscrizione è a rate, verifica che non ci siano rate scadute non pagate
  if (subscription.hasInstallments && Array.isArray(subscription.installments)) {
    const hasPastUnpaid = subscription.installments.some(inst => {
      if (!inst.dueDate) return false;
      const dueDate = new Date(inst.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < checkDate && !inst.paymentDate;
    });
    if (hasPastUnpaid) {
      return false;
    }
  }

  return true;
};

/**
 * Ottiene lo stato di una subscription
 * @param {Object} subscription - L'oggetto subscription
 * @param {Date} [date] - La data da verificare (default: oggi)
 * @returns {string} - Lo stato: 'valid', 'expiring', 'expired', 'future', 'incomplete'
 */
export const getSubscriptionStatus = (subscription, date = new Date()) => {
  if (!subscription || !subscription.startDate || !subscription.endDate) {
    return 'incomplete';
  }

  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);

  const startDate = new Date(subscription.startDate);
  const endDate = new Date(subscription.endDate);
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  if (checkDate < startDate) {
    return 'future';
  }

  // Se a rate, controlla se ci sono rate scadute non pagate
  if (subscription.hasInstallments && Array.isArray(subscription.installments)) {
    const hasPastUnpaid = subscription.installments.some(inst => {
      if (!inst.dueDate) return false;
      const dueDate = new Date(inst.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < checkDate && !inst.paymentDate;
    });

    if (hasPastUnpaid || checkDate > endDate) {
      return 'expired';
    }

    const thirtyDaysFromNow = new Date(checkDate);
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    const hasUpcomingUnpaid = subscription.installments.some(inst => {
      if (!inst.dueDate) return false;
      const dueDate = new Date(inst.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate >= checkDate && dueDate <= thirtyDaysFromNow && !inst.paymentDate;
    });

    if (hasUpcomingUnpaid) {
      return 'expiring';
    }

    if (checkDate <= endDate) {
      const daysUntilEnd = Math.ceil((endDate - checkDate) / (1000 * 60 * 60 * 24));
      if (daysUntilEnd <= 30) {
        return 'expiring';
      }
      return 'valid';
    }

    return 'expired';
  }

  // Iscrizione standard
  if (checkDate > endDate) {
    return 'expired';
  }

  const daysUntilEnd = Math.ceil((endDate - checkDate) / (1000 * 60 * 60 * 24));
  if (daysUntilEnd <= 30) {
    return 'expiring';
  }

  return 'valid';
};

/**
 * Ottiene le informazioni del badge per lo stato della subscription
 * @param {Object} subscription - L'oggetto subscription
 * @param {Date} [date] - La data da verificare (default: oggi)
 * @returns {Object} - Oggetto con variant e text per il badge
 */
export const getSubscriptionStatusBadge = (subscription, date = new Date()) => {
  const status = getSubscriptionStatus(subscription, date);
  
  switch (status) {
    case 'valid':
      return { variant: 'success', text: 'Valida' };
    case 'expiring':
      return { variant: 'warning', text: 'In scadenza' };
    case 'expired':
      return { variant: 'danger', text: 'Scaduta' };
    case 'future':
      return { variant: 'warning', text: 'Futura' };
    case 'incomplete':
    default:
      return { variant: 'secondary', text: 'Dati incompleti' };
  }
};

/**
 * Filtra le subscription valide per una data specifica
 * @param {Array} subscriptions - Array di subscription
 * @param {Date} [date] - La data da verificare (default: oggi)
 * @returns {Array} - Array delle subscription valide
 */
export const getValidSubscriptions = (subscriptions, date = new Date()) => {
  if (!subscriptions || !Array.isArray(subscriptions)) {
    return [];
  }
  
  return subscriptions.filter(sub => isSubscriptionValid(sub, date));
};

/**
 * Filtra le subscription scadute per una data specifica
 * @param {Array} subscriptions - Array di subscription
 * @param {Date} [date] - La data da verificare (default: oggi)
 * @returns {Array} - Array delle subscription scadute
 */
export const getExpiredSubscriptions = (subscriptions, date = new Date()) => {
  if (!subscriptions || !Array.isArray(subscriptions)) {
    return [];
  }
  
  return subscriptions.filter(sub => getSubscriptionStatus(sub, date) === 'expired');
};

/**
 * Conta le subscription valide per una data specifica
 * @param {Array} subscriptions - Array di subscription
 * @param {Date} [date] - La data da verificare (default: oggi)
 * @returns {number} - Numero di subscription valide
 */
export const countValidSubscriptions = (subscriptions, date = new Date()) => {
  return getValidSubscriptions(subscriptions, date).length;
};

/**
 * Conta le subscription scadute per una data specifica
 * @param {Array} subscriptions - Array di subscription
 * @param {Date} [date] - La data da verificare (default: oggi)
 * @returns {number} - Numero di subscription scadute
 */
export const countExpiredSubscriptions = (subscriptions, date = new Date()) => {
  return getExpiredSubscriptions(subscriptions, date).length;
};

/**
 * Genera il piano rate di default dividendo l'importo totale e le scadenze tra data inizio e data fine
 * @param {string|Date} startDate
 * @param {string|Date} endDate
 * @param {number} count - Numero di rate (default: 2)
 * @param {number} totalAmount - Importo totale da rateizzare
 * @returns {Array} - Elenco oggetti rata
 */
export const generateDefaultInstallments = (startDate, endDate, count = 2, totalAmount = 0) => {
  const n = Math.max(1, parseInt(count, 10) || 2);
  const total = Math.max(0, parseFloat(totalAmount) || 0);
  const baseAmount = Math.floor((total / n) * 100) / 100;
  const remainder = Math.round((total - (baseAmount * n)) * 100) / 100;

  const start = startDate ? new Date(startDate) : new Date();
  const end = endDate ? new Date(endDate) : new Date(start.getTime() + 90 * 86400000);
  const diffTime = Math.max(0, end.getTime() - start.getTime());

  const installments = [];
  for (let i = 1; i <= n; i++) {
    let dueDate;
    if (i === n) {
      dueDate = endDate ? new Date(endDate).toISOString().split('T')[0] : end.toISOString().split('T')[0];
    } else {
      const stepDate = new Date(start.getTime() + (diffTime * i) / n);
      dueDate = stepDate.toISOString().split('T')[0];
    }

    const amount = i === n ? Math.round((baseAmount + remainder) * 100) / 100 : baseAmount;

    installments.push({
      installmentNumber: i,
      amount,
      dueDate,
      paymentDate: '',
      notes: ''
    });
  }

  return installments;
};

/**
 * Suddivide un importo totale in quote uguali per il numero di rate indicato,
 * allocando gli eventuali centesimi di resto sull'ultima rata.
 * @param {number} count - Numero di rate
 * @param {number} totalAmount - Importo totale
 * @returns {Array<number>} - Array di importi
 */
export const generateEqualInstallmentAmounts = (count, totalAmount) => {
  const n = Math.max(1, parseInt(count, 10) || 1);
  const total = Math.max(0, parseFloat(totalAmount) || 0);
  const baseAmount = Math.floor((total / n) * 100) / 100;
  const remainder = Math.round((total - (baseAmount * n)) * 100) / 100;

  const amounts = [];
  for (let i = 1; i <= n; i++) {
    amounts.push(i === n ? Math.round((baseAmount + remainder) * 100) / 100 : baseAmount);
  }
  return amounts;
};

/**
 * Crea l'elenco rate per una sottoscrizione a partire dalle rate predefinite di un'offerta.
 * @param {Array} defaultInstallments - Rate predefinite dell'offerta
 * @returns {Array} - Array di rate pronte per l'iscrizione
 */
export const createInstallmentsFromOfferDefaults = (defaultInstallments) => {
  if (!Array.isArray(defaultInstallments) || defaultInstallments.length === 0) {
    return [];
  }

  return [...defaultInstallments]
    .sort((a, b) => (a.installmentNumber || 0) - (b.installmentNumber || 0))
    .map(di => ({
      installmentNumber: di.installmentNumber,
      amount: di.amount != null ? Number(di.amount) : 0,
      dueDate: di.dueDate
        ? (typeof di.dueDate === 'string'
            ? di.dueDate.split('T')[0]
            : new Date(di.dueDate).toISOString().split('T')[0])
        : '',
      paymentDate: '',
      notes: ''
    }));
};

/**
 * Trasla le date di scadenza di un elenco di rate di un numero specificato di giorni.
 * @param {Array} installments - Elenco oggetti rata
 * @param {number} daysDiff - Giorni di traslazione (positivi o negativi)
 * @returns {Array} - Nuovo array di rate con scadenze traslate
 */
export const shiftInstallmentDueDates = (installments, daysDiff) => {
  if (!Array.isArray(installments)) return [];
  if (!daysDiff || isNaN(daysDiff)) return installments.map(i => ({ ...i }));

  return installments.map(inst => {
    if (!inst.dueDate) return { ...inst };
    const d = new Date(inst.dueDate);
    if (isNaN(d.getTime())) return { ...inst };
    d.setDate(d.getDate() + daysDiff);
    return {
      ...inst,
      dueDate: d.toISOString().split('T')[0]
    };
  });
};

/**
 * Calcola la somma delle rate arrotondata a 2 decimali
 * @param {Array} installments
 * @returns {number}
 */
export const calculateInstallmentsSum = (installments) => {
  if (!Array.isArray(installments)) return 0;
  const sum = installments.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
  return Math.round(sum * 100) / 100;
};

/**
 * Calcola il periodo coperto da una determinata rata.
 * Per le rate intermedie: dalla scadenza della rata precedente (o startDate per la prima)
 * alla scadenza della rata corrente.
 * Per l'ultima rata: dalla scadenza della rata precedente fino alla fine del corso/iscrizione (endDate).
 *
 * @param {Object} installment - La rata corrente
 * @param {number} index - Indice della rata nell'elenco ordinato (0-based)
 * @param {Array} allInstallments - Elenco completo e ordinato delle rate
 * @param {string|Date} startDate - Data inizio iscrizione/corso
 * @param {string|Date} endDate - Data fine iscrizione/corso
 * @returns {{ start: string|Date|null, end: string|Date|null }}
 */
export const getInstallmentCoveragePeriod = (installment, index, allInstallments, startDate, endDate) => {
  const isFirst = index === 0;
  const isLast = !Array.isArray(allInstallments) || allInstallments.length === 0 || index === allInstallments.length - 1;

  const start = isFirst
    ? (startDate || installment?.dueDate || null)
    : (allInstallments[index - 1]?.dueDate || startDate || null);

  const end = isLast && endDate
    ? endDate
    : (installment?.dueDate || endDate || null);

  return { start, end };
};

/**
 * Restituisce il suffisso testuale descrittivo per la durata o tipologia dell'offerta.
/**
 * Verifica se un'offerta è a consumo (pay-per-use) basandosi su isPayPerUse o totalTickets > 0
 * @param {Object} offer - L'oggetto offerta
 * @returns {boolean}
 */
export const isPayPerUseOffer = (offer) => !!(offer?.isPayPerUse || (offer?.totalTickets != null && offer?.totalTickets > 0));

/**
 * Verifica se una subscription è a ingressi (da offerta o da totalTickets sull'iscrizione)
 * @param {Object} subscription - L'oggetto subscription
 * @param {Object} [offer] - L'oggetto offerta opzionale
 * @returns {boolean}
 */
export const isPayPerUseSubscription = (subscription, offer) => {
  if (subscription) {
    if (subscription.totalTickets != null && subscription.totalTickets > 0) return true;
    if (isPayPerUseOffer(subscription.offer)) return true;
  }
  const targetOffer = offer || subscription?.offer || subscription;
  if (targetOffer) {
    if (targetOffer.totalTickets != null && targetOffer.totalTickets > 0) return true;
    if (isPayPerUseOffer(targetOffer)) return true;
  }
  return false;
};

/**
 * Verifica se una subscription o offerta è di prova (trial)
 * @param {Object} item - Subscription oppure offerta
 * @param {Object} [secondItem] - Secondo oggetto opzionale
 * @returns {boolean}
 */
export const isTrialSubscription = (item, secondItem) => {
  const check = (obj) => {
    if (!obj) return false;
    return Boolean(
      obj.isTrial ||
      obj.offer?.isTrial ||
      (obj.name && obj.name.toLowerCase().includes('prova')) ||
      (obj.offer?.name && obj.offer.name.toLowerCase().includes('prova'))
    );
  };
  return check(item) || check(secondItem);
};

/**
 * Verifica se un'offerta è mensile basandosi su isMonthly o durationMonths > 0
 * @param {Object} offer - L'oggetto offerta
 * @returns {boolean}
 */
export const isMonthlyOffer = (offer) => !isPayPerUseOffer(offer) && !!(offer?.isMonthly || (offer?.durationMonths != null && offer?.durationMonths > 0));

/**
 * Restituisce la durata formattata tra parentesi per un'offerta.
 * Per offerte a consumo: (X lezioni)
 * Per offerte mensili: (1 mese) oppure (X mesi)
 * Per offerte a data libera: (Data Libera)
 * @param {Object} offer - L'oggetto offerta
 * @returns {string} - Stringa formattata con parentesi
 */
export const formatOfferDuration = (offer) => {
  if (!offer) return '';
  if (isPayPerUseOffer(offer)) {
    return `(${offer.totalTickets || 0} lezioni)`;
  }
  if (isMonthlyOffer(offer)) {
    const months = offer.durationMonths || 0;
    return `(${months} mes${months === 1 ? 'e' : 'i'})`;
  }
  return '(Data Libera)';
};

/**
 * Restituisce l'etichetta completa per la selezione dell'offerta (es. nei select/dropdown).
 * Es: "Nome Offerta - €845.00 (Data Libera)"
 * @param {Object} offer - L'oggetto offerta
 * @returns {string} - Etichetta formattata
 */
export const formatOfferLabel = (offer) => {
  if (!offer) return '';
  const durationStr = formatOfferDuration(offer);
  const amountStr = offer.amount != null ? ` - €${Number(offer.amount).toFixed(2)}` : '';
  return `${offer.name || ''}${amountStr} ${durationStr}`.trim();
};

/**
 * Restituisce la descrizione del tipo di offerta (per tabelle o export CSV).
 * Es: "Pay-per-use", "Mensile (3 mesi)", "Data Libera"
 * @param {Object} offer - L'oggetto offerta
 * @param {Object} [subscription] - L'oggetto iscrizione opzionale
 * @returns {string} - Descrizione del tipo
 */
export const formatOfferType = (offer, subscription) => {
  if (isTrialSubscription(subscription) || isTrialSubscription(offer)) {
    return 'Prova';
  }
  if (!offer && (!subscription || (!subscription.offer && !subscription.totalTickets))) {
    return 'Data Libera';
  }
  if (isPayPerUseSubscription(subscription, offer)) {
    return 'Pay-per-use';
  }
  if (isMonthlyOffer(offer || subscription?.offer)) {
    const targetOffer = offer || subscription?.offer;
    const months = targetOffer.durationMonths || 0;
    return `Mensile (${months} mes${months === 1 ? 'e' : 'i'})`;
  }
  return 'Data Libera';
};

/**
 * Metodi di pagamento supportati per gli abbonamenti
 */
export const PAYMENT_METHODS = [
  { value: 'cash', label: 'Contanti' },
  { value: 'bank_transfer', label: 'Bonifico' },
  { value: 'card', label: 'Carta' },
  { value: 'check', label: 'Assegno' }
];

/**
 * Formatta il metodo di pagamento per la visualizzazione o l'export CSV
 * @param {string} [method] - Il codice del metodo di pagamento ('cash', 'bank_transfer', 'card', 'check')
 * @returns {string} - L'etichetta in italiano o '-' se assente
 */
export const formatPaymentMethod = (method) => {
  switch (method) {
    case 'cash':
      return 'Contanti';
    case 'bank_transfer':
      return 'Bonifico';
    case 'card':
      return 'Carta';
    case 'check':
      return 'Assegno';
    default:
      return method || '-';
  }
};
